import { useEffect, useMemo } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { isolateName, SEND_HOLD_LINGER_MS, sendHoldCoveredLiveState, sendHoldLiveView, type SendHoldCard } from '../../../lib/send-hold';
import { monotonicNow, noteHoldSeen, useHeldSends, useOnScreen, type HeldSendsScope } from '../../../lib/use-held-sends';
import { HeldSendRow, useChannelLabel } from './HeldSendRow';
import styles from './HeldSendRow.module.css';

/**
 * The chat chip: one row per message the turn left WAITING (`metadata.sendHoldCard` on the assistant row, the send
 * rule's contract `native-apps/shared/send-hold/contract.json`). Drawn from its OWN row, so a newer reply never
 * closes it; mirrored by iOS/Android `HeldSendChips`.
 *
 * The live state is ONE read per conversation (`/api/held-sends?conversationId=`), made only while some hold is
 * still in its window, again when the cached read started before a hold was first seen, and once more at each
 * hold's zero. A read that failed, did not cover the hold, or was partial or truncated keeps every Cancel
 * (`sendHoldCoveredLiveState`).
 *
 * `meeting` renders the meeting summary's card (`metadata.meetingHoldsCard`, meeting hold M6) with the same rows:
 * its live read is BY THE CARD'S HOLD IDS (`/api/held-sends?ids=`), never by the summary row's conversation — a
 * meeting hold belongs to the sub-agent's brain chat, so that read would never list it and would end every hold.
 * Either way the send time comes from the live row whenever one is read (S2), and Send now is offered only while the
 * live read (or, before one covers the hold, the card) says so (`sendHoldLiveView`).
 */
export function HeldSendChips({ card, conversationId, meeting = false }: { card: SendHoldCard; conversationId?: string; meeting?: boolean }) {
  const { t } = useTranslation();
  const channelLabel = useChannelLabel();
  // Read once per mount: a chip whose window closed before the screen opened needs no live read at all.
  const anyOpen = useMemo(() => card.holds.some((h) => Date.parse(h.fireAt) + SEND_HOLD_LINGER_MS > Date.now()), [card]);
  const holdIds = useMemo(() => card.holds.map((h) => h.holdId), [card]);
  const scope: HeldSendsScope | undefined = meeting ? { ids: holdIds } : conversationId ? { conversationId } : undefined;
  // The meeting card reads by its ids while it is ON SCREEN, whatever the card's stamped times say (M6): the summary's
  // clock moves a hold LATER, so a card whose stamped times all passed may still hold a live one — only the read knows.
  const [ref, onScreen] = useOnScreen<HTMLDivElement>();
  const readEnabled = meeting ? onScreen : anyOpen;
  const { list, readStartedAt, fetching, refetch } = useHeldSends(scope, readEnabled);
  const seenAt = useMemo(() => new Map(card.holds.map((h) => [h.holdId, noteHoldSeen(h.holdId)])), [card]);
  // A list cached from an earlier turn (or a read in flight when this reply landed) does not speak for these holds:
  // read again, once, rather than let a stale "not held" end them.
  const uncovered = readEnabled && list != null && card.holds.some((h) => (readStartedAt ?? 0) < (seenAt.get(h.holdId) ?? 0));
  useEffect(() => {
    if (uncovered && !fetching) refetch();
  }, [uncovered, fetching, refetch]);
  return (
    <div ref={ref} className={styles.chips} data-testid={meeting ? 'meeting-held-sends' : undefined}>
      {card.holds.map((hold) => {
        const covered = sendHoldCoveredLiveState(hold.holdId, list, readStartedAt, seenAt.get(hold.holdId) ?? monotonicNow()) !== 'unknown';
        const view = sendHoldLiveView(hold, covered ? list : undefined);
        return (
          <HeldSendRow
            key={hold.holdId}
            holdId={hold.holdId}
            fireAt={view.fireAt}
            bucket={hold.bucket}
            title={t('sendHold.to', { name: isolateName(hold.to), channel: channelLabel(hold.channel) })}
            surface={meeting ? 'meeting' : 'chat'}
            live={view.live}
            canCancel={hold.actions.includes('cancel')}
            offersSendNow={view.offersSendNow}
            onZero={refetch}
            onSettled={refetch}
          />
        );
      })}
    </div>
  );
}
